/**
 * Brevo Conversations programmatic API for the client/ app.
 *
 * Single Brevo workspace, one chatbot. The bot in Brevo's dashboard
 * branches on the visitor attribute `user_role`, which we set per audience:
 *
 *    'agent'           — external broker on the agent_dashboard side
 *    'pm'              — property manager (logged in on client/)
 *    'internal_agent'  — Leasehub internal agent (logged in on client/)
 *    'applicant'       — public applicant on a lead form / public listing
 *    'tenant'          — current tenant confirming/rescheduling a showing
 *    'guarantor'       — guarantor completing the guarantor flow
 *
 * Call setBrevoUserRole(role, extraContext) once per audience surface so the bot
 * picks the right branch as soon as the chat opens.
 */

const DEFAULT_CONVERSATIONS_ID = '65f824590266d852505f892d';
const CONVERSATIONS_ID =
    import.meta.env.VITE_BREVO_CONVERSATIONS_ID || DEFAULT_CONVERSATIONS_ID;

const CONTACT_QUERIES_SCENARIO_ID =
    import.meta.env.VITE_BREVO_CONTACT_QUERIES_SCENARIO_ID || '';
const TENANT_FLOW_SCENARIO_ID = import.meta.env.VITE_BREVO_TENANT_FLOW_SCENARIO_ID || '';
const PM_INTERNAL_FLOW_SCENARIO_ID =
    import.meta.env.VITE_BREVO_PM_INTERNAL_FLOW_SCENARIO_ID || '';
const ROLE_PICKER_SCENARIO_ID =
    import.meta.env.VITE_BREVO_UNKNOWN_ROLE_SCENARIO_ID ||
    import.meta.env.VITE_BREVO_ROLE_PICKER_SCENARIO_ID ||
    '';

const ROLE_TO_SCENARIO = Object.freeze({
    agent: CONTACT_QUERIES_SCENARIO_ID,
    applicant: CONTACT_QUERIES_SCENARIO_ID,
    guarantor: CONTACT_QUERIES_SCENARIO_ID,
    tenant: TENANT_FLOW_SCENARIO_ID,
    pm: PM_INTERNAL_FLOW_SCENARIO_ID,
    internal_agent: PM_INTERNAL_FLOW_SCENARIO_ID,
});

const ROLE_PICKER_REPLY_TO_ROLE = Object.freeze({
    agent: 'agent',
    'im an agent': 'agent',
    applicant: 'applicant',
    'im an applicant': 'applicant',
    tenant: 'tenant',
    'im a tenant': 'tenant',
    guarantor: 'guarantor',
    'im a guarantor': 'guarantor',
    pm: 'pm',
    'property manager': 'pm',
    'im a property manager': 'pm',
    'internal agent': 'internal_agent',
    'im an internal agent': 'internal_agent',
});

const SCRIPT_ID = 'brevo-conversations-script';
const HIDDEN_LAUNCHER_ID = 'brevo-hidden-launcher';
const READY_FALLBACK_MS = 1200;

let readyPromise = null;
let currentContext = {};
let scenarioListenersRegistered = false;
let lastStartedScenarioId = null;
let lastStartedScenarioRole = null;
let isChatOpen = false;
let shouldRestartScenarioOnOpen = false;

function scenarioDebugValue(scenarioId) {
    if (!scenarioId) return { configured: false };
    return {
        configured: true,
        suffix: scenarioId.slice(-6),
    };
}

function brevoDebugPayload(patch) {
    const isProduction = import.meta.env.MODE === 'production';
    const base = {
        conversationId: CONVERSATIONS_ID,
        loaded: Boolean(window.BrevoConversations?.loaded),
        role: currentContext.user_role,
        page: currentContext.page,
        source: currentContext.source,
        clientId: currentContext.client_id,
        publicPath: currentContext.public_path,
        scenarioConfig: {
            contactQueries: scenarioDebugValue(CONTACT_QUERIES_SCENARIO_ID),
            tenantFlow: scenarioDebugValue(TENANT_FLOW_SCENARIO_ID),
            pmInternalFlow: scenarioDebugValue(PM_INTERNAL_FLOW_SCENARIO_ID),
            rolePicker: scenarioDebugValue(ROLE_PICKER_SCENARIO_ID),
        },
    };

    if (isProduction) {
        return {
            ...base,
            ...patch,
        };
    }

    return {
        ...base,
        context: currentContext,
        ...(window.__leasehubBrevoDebug || {}),
        ...patch,
    };
}

function updateBrevoDebug(patch) {
    if (typeof window === 'undefined') return;
    window.__leasehubBrevoDebug = brevoDebugPayload(patch);
}

function currentBrevoRole() {
    return typeof currentContext.user_role === 'string' ? currentContext.user_role : undefined;
}

function syncBrevoContext() {
    if (
        typeof window !== 'undefined' &&
        window.BrevoConversations &&
        Object.keys(currentContext).length > 0
    ) {
        window.BrevoConversations('updateIntegrationData', currentContext);
    }
}

function ensureHiddenLauncher() {
    if (typeof document === 'undefined') return;
    let hiddenLauncher = document.getElementById(HIDDEN_LAUNCHER_ID);
    if (!hiddenLauncher) {
        hiddenLauncher = document.createElement('button');
        hiddenLauncher.id = HIDDEN_LAUNCHER_ID;
        hiddenLauncher.type = 'button';
        hiddenLauncher.setAttribute('aria-hidden', 'true');
        hiddenLauncher.style.display = 'none';
    }
    if (!document.body.contains(hiddenLauncher)) {
        document.body.appendChild(hiddenLauncher);
    }
}

function configureBrevoLauncher({ hideLauncher = false } = {}) {
    if (!hideLauncher || typeof window === 'undefined') return;
    ensureHiddenLauncher();
    window.BrevoConversationsSetup = {
        ...(window.BrevoConversationsSetup || {}),
        customWidgetButton: `#${HIDDEN_LAUNCHER_ID}`,
    };
}

function normalizeRolePickerText(text) {
    return String(text || '')
        .replace(/[’‘]/g, "'")
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, ' ')
        .trim();
}

function roleFromRolePickerText(text) {
    return ROLE_PICKER_REPLY_TO_ROLE[normalizeRolePickerText(text)];
}

function scenarioIdForRole(role) {
    return (role && ROLE_TO_SCENARIO[role]) || ROLE_PICKER_SCENARIO_ID;
}

function handleBrevoNewMessage(message) {
    if (!message || message.type !== 'visitor') return;
    const role = roleFromRolePickerText(message.text);
    if (!role) return;

    setBrevoUserRole(role, {
        role_picker_selection: message.text,
        role_picker_source: 'brevo_message',
    });
    void startRoleBot(role, { force: true, source: 'role_picker' });
}

function handleBrevoAnalyticEvent(eventName) {
    const normalizedEventName = String(eventName || '').toLowerCase();
    if (normalizedEventName.includes('open')) {
        isChatOpen = true;
    }
    if (
        normalizedEventName.includes('close') ||
        normalizedEventName.includes('closed') ||
        normalizedEventName.includes('minimize')
    ) {
        isChatOpen = false;
        shouldRestartScenarioOnOpen = Boolean(lastStartedScenarioId);
    }
    updateBrevoDebug({
        chatOpen: isChatOpen,
        lastBrevoEvent: eventName,
        shouldRestartScenarioOnOpen,
    });
}

function registerBrevoScenarioListeners() {
    if (typeof window === 'undefined' || scenarioListenersRegistered) return;
    scenarioListenersRegistered = true;

    const existingSetup = window.BrevoConversationsSetup || {};
    const previousOnNewMessage = existingSetup.onNewMessage;
    const previousOnAnalyticEvent = existingSetup.onAnalyticEvent;

    window.BrevoConversationsSetup = {
        ...existingSetup,
        onNewMessage(message) {
            if (typeof previousOnNewMessage === 'function') {
                previousOnNewMessage(message);
            }
            handleBrevoNewMessage(message);
        },
        onAnalyticEvent(eventName) {
            if (typeof previousOnAnalyticEvent === 'function') {
                previousOnAnalyticEvent(eventName);
            }
            handleBrevoAnalyticEvent(eventName);
        },
    };
}

/**
 * Load the Brevo widget script. Idempotent — safe to call repeatedly.
 */
export function loadBrevoWidget(options = {}) {
    if (typeof window === 'undefined') return Promise.resolve();
    if (window.BrevoConversations?.loaded) {
        updateBrevoDebug({ loaded: true, status: 'loaded' });
        return Promise.resolve();
    }
    if (readyPromise) {
        updateBrevoDebug({ status: 'loading' });
        return readyPromise;
    }

    readyPromise = new Promise((resolve) => {
        let resolved = false;
        const markReady = () => {
            if (resolved) return;
            resolved = true;
            if (window.BrevoConversations) window.BrevoConversations.loaded = true;
            syncBrevoContext();
            updateBrevoDebug({ loaded: true, status: 'loaded' });
            resolve();
        };

        window.BrevoConversationsID = CONVERSATIONS_ID;
        updateBrevoDebug({ loaded: false, status: 'loading' });
        registerBrevoScenarioListeners();
        configureBrevoLauncher(options);

        window.BrevoConversations =
            window.BrevoConversations ||
            function () {
                (window.BrevoConversations.q = window.BrevoConversations.q || []).push(
                    arguments
                );
            };

        window.BrevoConversations('onReady', markReady);
        syncBrevoContext();

        const existing = document.getElementById(SCRIPT_ID);
        if (existing) {
            window.setTimeout(markReady, READY_FALLBACK_MS);
            return;
        }
        const script = document.createElement('script');
        script.id = SCRIPT_ID;
        script.async = true;
        script.src = 'https://conversations-widget.brevo.com/brevo-conversations.js';
        script.onload = () => window.setTimeout(markReady, READY_FALLBACK_MS);
        script.onerror = () => {
            readyPromise = null;
            updateBrevoDebug({ loaded: false, status: 'error' });
            resolve();
        };
        document.head.appendChild(script);
    });

    return readyPromise;
}

/**
 * Attach visitor context that shows up in the conversation sidebar AND
 * is available to the chatbot for branching logic.
 *
 * Safe to call before loadBrevoWidget. The context is synced when the widget is
 * explicitly loaded by a public page widget or support button.
 */
export function setBrevoContext(data) {
    if (typeof window === 'undefined') return;
    try {
        currentContext = {
            ...currentContext,
            ...(data || {}),
        };
        updateBrevoDebug({
            context: currentContext,
            role: currentContext.user_role,
            status: window.BrevoConversations?.loaded ? 'loaded' : 'queued',
        });
        syncBrevoContext();
    } catch {
        // never crash the host page on a chat-tool failure
    }
}

/**
 * Convenience: set the visitor's role and any extra context.
 * The Brevo bot is configured to branch on the `user_role` attribute.
 */
export function setBrevoUserRole(role, extraContext) {
    setBrevoContext({
        user_role: role,
        ...(extraContext || {}),
    });
}

/**
 * Start the chatbot scenario for the current role.
 *
 * Brevo's documented method is startBotScenario(scenarioId). If a role has no
 * configured scenario ID, fall back to the unknown-role picker, not Contact
 * Queries. Missing fallback IDs are treated as no-ops.
 */
export async function startRoleBot(role = currentBrevoRole(), options = {}) {
    if (typeof window === 'undefined') return false;

    const scenarioId = scenarioIdForRole(role);
    if (!scenarioId) {
        updateBrevoDebug({
            scenarioStatus: 'missing_scenario',
            scenarioRole: role || 'unknown',
        });
        return false;
    }

    await loadBrevoWidget();

    if (!options.force && lastStartedScenarioId === scenarioId && lastStartedScenarioRole === role) {
        updateBrevoDebug({
            scenarioStatus: 'already_started',
            scenarioId,
            scenarioRole: role,
            chatOpen: isChatOpen,
        });
        return true;
    }

    lastStartedScenarioId = scenarioId;
    lastStartedScenarioRole = role || null;
    shouldRestartScenarioOnOpen = false;
    updateBrevoDebug({
        scenarioStatus: 'started',
        scenarioId,
        scenarioRole: role || 'unknown',
        scenarioSource: options.source,
        chatOpen: isChatOpen,
        shouldRestartScenarioOnOpen,
    });
    window.BrevoConversations?.('startBotScenario', scenarioId);
    return true;
}

/**
 * Open the Brevo chat. Pass options:
 *   - role: 'agent' | 'pm' | 'internal_agent' | 'applicant' | 'tenant' | 'guarantor'
 *   - context: any extra visitor attributes (page, listing_id, topic, etc.)
 */
export async function openBrevoChat(options = {}) {
    if (options.role) {
        setBrevoUserRole(options.role, options.context);
    } else if (options.context) {
        setBrevoContext(options.context);
    }
    const role = currentBrevoRole();
    const scenarioId = scenarioIdForRole(role);
    const forceRestart = shouldRestartScenarioOnOpen && lastStartedScenarioId === scenarioId;
    await loadBrevoWidget({ hideLauncher: true });
    updateBrevoDebug({
        lastOpenAt: new Date().toISOString(),
        chatOpen: true,
        forceRestart,
    });
    window.BrevoConversations?.('openChat', true);
    isChatOpen = true;
    await startRoleBot(role, { force: forceRestart, source: 'open_chat' });
}
